import {EmployeeLdap} from 'client';
import {
  $getSelection,
  $setSelection,
  type LexicalEditor,
  SKIP_COLLAB_TAG,
  SKIP_DOM_SELECTION_TAG,
} from 'lexical';
import {createContext, ReactNode, useContext, useMemo, useRef} from 'react';

// Context for sharing collabId and related data
interface CollabContextType {
  collabOwner?: EmployeeLdap;
  collabId?: string;
  planningId?: string;
  commentsDocumentId?: string;
}

const CollabContext = createContext<CollabContextType>({});

// Hook to use the collab context
export const useCollabContext = () => {
  const context = useContext(CollabContext);
  if (context === undefined) {
    throw new Error('useCollabContext must be used within a CollabProvider');
  }
  return context;
};

export const CollabProvider = ({
  collabOwner,
  collabId,
  planningId,
  commentsDocumentId,
  children,
}: {
  collabOwner?: EmployeeLdap;
  collabId?: string;
  planningId?: string;
  commentsDocumentId?: string;
  children: ReactNode;
}) => {
  return (
    <CollabContext.Provider
      value={{
        collabOwner,
        collabId,
        planningId,
        commentsDocumentId,
      }}
    >
      {children}
    </CollabContext.Provider>
  );
};

/**
 * Hook to get the current planning document
 */
export function usePlanningId(): string | undefined {
  const {planningId} = useCollabContext();
  return planningId;
}

export function useCollabId(): string | undefined {
  const {collabId, commentsDocumentId} = useCollabContext();
  return collabId || commentsDocumentId;
}

/**
 * Manages focus for multiple Lexical editors to prevent focus conflicts.
 * Used in planning documents where multiple editable fields exist side-by-side (e.g. StrategyKernel).
 */
export interface EditorFocusManager {
  registerEditor: (id: string, editor: LexicalEditor) => void;
  unregisterEditor: (id: string) => void;
  clearAllExcept: (exceptId: string) => void;
  clearAll: () => void;
}

export const EditorFocusManagerContext = createContext<EditorFocusManager | null>(null);

/**
 * Hook to access editor focus manager.
 * Throws error if not within a focus manager provider and component is trying to access it.
 */
export function useEditorFocusManager(): EditorFocusManager {
  const context = useContext(EditorFocusManagerContext);
  if (context === null) {
    throw new Error(
      'useEditorFocusManager must be used within a <EditorFocusManagerContext.Provider>'
    );
  }
  return context;
}

export function useOptionalEditorFocusManager(): EditorFocusManager | null {
  return useContext(EditorFocusManagerContext);
}

/**
 * Hook to create an editor focus manager implementation.
 * Use this in components that need to coordinate focus across multiple child editors.
 *
 * @param rootEditor - The root editor whose selection should also be cleared. If null, no root editor focus will be cleared.
 * @returns EditorFocusManager with register/unregister/clearAllExcept/clearAll methods
 *
 * @example
 * ```tsx
 * const focusManager = useEditorFocusManagerProvider(editor);
 * return (
 *   <EditorFocusManagerContext.Provider value={focusManager}>
 *     {children}
 *   </EditorFocusManagerContext.Provider>
 * );
 * ```
 */
export function useEditorFocusManagerProvider(
  rootEditor: LexicalEditor | null
): EditorFocusManager {
  const editorsRef = useRef<Map<string, LexicalEditor>>(new Map());

  return useMemo(() => {
    const clearEditorSelection = (editor: LexicalEditor) => {
      editor.update(
        () => {
          if ($getSelection() !== null) {
            $setSelection(null);
          }
        },
        {discrete: true, tag: [SKIP_COLLAB_TAG, SKIP_DOM_SELECTION_TAG]}
      );
      // Also blur the DOM element to fully release focus
      editor.getRootElement()?.blur();
    };

    const clearChildEditors = (exceptId: string | null) => {
      editorsRef.current.forEach((childEditor, id) => {
        if (exceptId === null || id !== exceptId) {
          try {
            clearEditorSelection(childEditor);
          } catch (err) {
            // Editor might be disposed, ignore
            console.warn(`Failed to clear editor ${id}:`, err);
          }
        }
      });
    };

    return {
      registerEditor: (id: string, editor: LexicalEditor) => {
        editorsRef.current.set(id, editor);
      },
      unregisterEditor: (id: string) => {
        editorsRef.current.delete(id);
      },
      clearAllExcept: (exceptId: string) => {
        if (rootEditor) {
          clearEditorSelection(rootEditor);
        }
        clearChildEditors(exceptId);
      },
      clearAll: () => {
        if (rootEditor) {
          clearEditorSelection(rootEditor);
        }
        clearChildEditors(null);
      },
    };
  }, [rootEditor]);
}
